import React, { useState, useEffect, useMemo } from "react";
import { LogoPulse } from "../Loader";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { TEAM_INFO } from "../../lib/teamMap";
import { TeamBadge } from "../../components/TeamBadge";
import { Download, Save, Trash2, AlertTriangle, RefreshCw, Link2, Users, UserPlus } from "lucide-react";
import { inputCls } from "./shared";

// ===================== PWHL GAME CENTRE SYNC (Phase 1) =====================
// Staging-only importer. Fetches the official box score, matches players, and
// lets an admin review + save into the isolated `pwhl_imports` collection.
// Touches NO season stats / logs.
//
// This module exposes three pieces so the controls can live CONTEXTUALLY in
// the public Game Centre (a slim import bar + a matching block next to the
// player box) instead of one giant admin panel:
//   - usePWHLSync(...)   shared state + actions
//   - <PWHLSyncControl>  compact import / re-sync bar
//   - <PWHLMatchReview>  player-matching review table
// The default export keeps the full panel (used on the Admin Game Centre page).

const abbr = (tid) => TEAM_INFO[tid]?.abbr || (tid || "").toUpperCase() || "?";
const SYNC_STATUS = {
    not_linked: { label: "Not linked", cls: "text-[#64748B] border-[#64748B]/40" },
    linked: { label: "Linked", cls: "text-[#7FD3E6] border-[#7FD3E6]/40" },
    synced: { label: "Synced", cls: "text-[#22C55E] border-[#22C55E]/40" },
    live: { label: "Live", cls: "text-[#FBBF24] border-[#FBBF24]/40" },
    final: { label: "Final · Synced", cls: "text-[#22C55E] border-[#22C55E]/40" },
    error: { label: "Error", cls: "text-[#FB7185] border-[#FB7185]/40" },
};
const fmtTime = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
};
const MATCH_BADGE = {
    name_team: { label: "matched", cls: "text-[#22C55E] border-[#22C55E]/40" },
    name: { label: "matched", cls: "text-[#22C55E] border-[#22C55E]/40" },
    none: { label: "unmatched", cls: "text-[#FB7185] border-[#FB7185]/40" },
    manual: { label: "manual", cls: "text-[#9D4CDD] border-[#9D4CDD]/40" },
};

// ===================== shared sync hook =====================
export function usePWHLSync({ gameId, fixture, enabled = true }) {
    const [url, setUrl] = useState("");
    const [preview, setPreview] = useState(null);
    const [overrides, setOverrides] = useState({}); // pwhl_id -> rosterly player id
    const [busy, setBusy] = useState("");
    const [msg, setMsg] = useState("");
    const [promotion, setPromotion] = useState(null);

    const playersQ = useQuery({
        queryKey: ["pwhl-sync-players"],
        queryFn: () => api.get(`/players?include_inactive=true`).then((r) => r.data),
        enabled,
    });
    const savedQ = useQuery({
        queryKey: ["pwhl-import", gameId],
        queryFn: () => api.get(`/admin/games/${gameId}/pwhl-import`).then((r) => r.data),
        enabled: !!gameId && enabled,
    });

    useEffect(() => {
        const s = savedQ.data;
        if (s && s.pwhl_game_id) {
            setPreview(s);
            setUrl(s.source_url || "");
            const o = {};
            (s.people || []).forEach((p) => { if (p.matched_player_id) o[p.pwhl_id] = p.matched_player_id; });
            setOverrides(o);
        }
    }, [savedQ.data]);

    const players = useMemo(() => playersQ.data || [], [playersQ.data]);
    const playerById = useMemo(() => Object.fromEntries(players.map((p) => [p.id, p])), [players]);
    const playersByTeam = useMemo(() => {
        const m = {};
        players.forEach((p) => { (m[p.team_id] = m[p.team_id] || []).push(p); });
        return m;
    }, [players]);

    const fetchPreview = async () => {
        if (!url.trim()) return;
        setBusy("fetch"); setMsg("");
        try {
            const r = await api.post(`/admin/games/${gameId}/pwhl-fetch`, { url });
            setPreview(r.data);
            const o = {};
            (r.data.people || []).forEach((p) => { if (p.matched_player_id) o[p.pwhl_id] = p.matched_player_id; });
            setOverrides(o);
            setMsg(`Fetched PWHL game #${r.data.pwhl_game_id} · ${r.data.match_summary.matched}/${r.data.match_summary.total} players matched`);
        } catch (e) {
            setMsg(`Error: ${e?.response?.data?.detail || e.message}`);
        } finally { setBusy(""); }
    };

    const saveImport = async () => {
        if (!preview) return;
        setBusy("save"); setMsg("");
        const people = (preview.people || []).map((p) => ({
            ...p,
            matched_player_id: overrides[p.pwhl_id] ?? p.matched_player_id ?? "",
            match_method: overrides[p.pwhl_id] && overrides[p.pwhl_id] !== p.matched_player_id ? "manual" : p.match_method,
        }));
        try {
            const res = await api.post(`/admin/games/${gameId}/pwhl-import`, {
                ...preview, people,
                season: fixture?.season || preview.season || "",
                stage: fixture?.stage || preview.stage || "",
            });
            setPromotion(res.data?.promotion || null);
            savedQ.refetch();
            const promo = res.data?.promotion || {};
            const um = promo.unmatched_count || 0;
            setMsg(`Synced — ${promo.skaters || 0} skaters & ${promo.goalies || 0} goalies promoted to leaderboards & insights.${um ? ` ${um} unmatched (match or create below, then Save again).` : ""}`);
        } catch (e) {
            setMsg(`Error: ${e?.response?.data?.detail || e.message}`);
        } finally { setBusy(""); }
    };

    const createPlayer = async (person) => {
        const posRaw = (person.position || "").toUpperCase();
        const position = posRaw === "G" ? "G" : (/D/.test(posRaw) ? "D" : "F");
        const r = await api.post(`/admin/players`, {
            name: person.name, team_id: person.team_id, position,
            jersey: parseInt(person.jersey, 10) || 0, league: "PWHL",
        });
        setOverride(person.pwhl_id, r.data.id);
        await playersQ.refetch();
        return r.data.id;
    };

    const clearImport = async () => {
        if (!window.confirm("Remove this synced game completely? This deletes the saved import AND rolls back the promoted box score — goals, player game logs, standings and all goal templates for this game are cleared. The fixture stays (reset to unplayed).")) return;
        setBusy("clear"); setMsg("");
        try {
            await api.delete(`/admin/games/${gameId}/pwhl-import`);
            setPreview(null); setUrl(""); setOverrides({});
            savedQ.refetch();
            setMsg("Synced game removed — box score, logs & goal templates fully cleared.");
        } catch (e) {
            setMsg(`Error: ${e?.response?.data?.detail || e.message}`);
        } finally { setBusy(""); }
    };

    const setOverride = (pwhlId, pid) => setOverrides((o) => ({ ...o, [pwhlId]: pid }));

    const saved = savedQ.data;
    const statusKey = saved?.pwhl_game_id ? (saved.sync_status || "synced") : (preview ? "linked" : "not_linked");
    const statusBadge = SYNC_STATUS[statusKey] || SYNC_STATUS.not_linked;
    const resync = async () => {
        if (saved?.source_url) setUrl(saved.source_url);
        await fetchPreview();
    };

    const matchCount = useMemo(() => {
        const ppl = preview?.people || [];
        const matched = ppl.filter((p) => (overrides[p.pwhl_id] ?? p.matched_player_id ?? "")).length;
        return { matched, total: ppl.length, unmatched: ppl.length - matched };
    }, [preview, overrides]);

    return {
        url, setUrl, preview, overrides, setOverride, busy, msg,
        saved, savedQ, statusBadge, matchCount, promotion,
        players, playerById, playersByTeam,
        fetchPreview, saveImport, clearImport, resync, createPlayer,
    };
}

// ===================== compact import / re-sync bar =====================
export function PWHLSyncControl({ sync }) {
    const { url, setUrl, preview, busy, msg, saved, savedQ, statusBadge, fetchPreview, saveImport, clearImport, resync } = sync;
    return (
        <div className="space-y-2" data-testid="pwhl-sync-control">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="font-heading text-[11px] font-bold uppercase tracking-[0.18em] text-white flex items-center gap-1.5">
                    <Link2 size={13} className="text-[#9D4CDD]" /> PWHL Sync
                </span>
                <span className={`uppercase font-bold text-[9px] px-1.5 py-0.5 rounded border ${statusBadge.cls}`} data-testid="pwhl-sync-status">{statusBadge.label}</span>
                {saved?.pwhl_game_id && <span className="text-[10px] text-[#64748B]">#{saved.pwhl_game_id}</span>}
                {savedQ?.data?.last_synced_at && <span className="text-[10px] text-[#64748B]" data-testid="pwhl-last-sync">· {fmtTime(savedQ.data.last_synced_at)}</span>}
                {saved?.pwhl_game_id && (
                    <button type="button" onClick={resync} disabled={busy === "fetch"} className="text-[#9D4CDD] hover:underline flex items-center gap-1 text-[11px]" data-testid="pwhl-resync-btn">
                        {busy === "fetch" ? <LogoPulse className="h-[11px] w-[11px]" /> : <RefreshCw size={11} />} Re-sync now
                    </button>
                )}
                <span className="text-[9px] text-[#9D4CDD] font-bold ml-auto">STAGING ONLY · NO STATS TOUCHED</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
                <input
                    value={url} onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://www.thepwhl.com/en/stats/game-center/210"
                    className={inputCls + " flex-1 min-w-[240px] text-xs py-1.5"}
                    data-testid="pwhl-url-input"
                />
                <button type="button" onClick={fetchPreview} disabled={busy === "fetch" || !url.trim()}
                    className="bg-[#9D4CDD] text-white px-3 py-1.5 rounded font-bold uppercase tracking-wider text-[11px] hover:bg-[#7E3DB0] disabled:opacity-50 flex items-center gap-1.5"
                    data-testid="pwhl-fetch-btn">
                    {busy === "fetch" ? <LogoPulse className="h-[12px] w-[12px]" /> : <Download size={12} />}
                    {busy === "fetch" ? "Fetching…" : "Fetch"}
                </button>
                {preview && (
                    <>
                        <button type="button" onClick={saveImport} disabled={busy === "save"}
                            className="border border-[#22C55E]/50 text-[#22C55E] hover:bg-[#22C55E]/10 px-2.5 py-1.5 rounded text-[11px] uppercase tracking-wider font-bold disabled:opacity-50 flex items-center gap-1.5"
                            data-testid="pwhl-save-btn">
                            <Save size={12} /> {busy === "save" ? "Saving…" : "Save"}
                        </button>
                        <button type="button" onClick={clearImport} disabled={busy === "clear"}
                            className="text-[#FB7185] hover:underline text-[11px] flex items-center gap-1" data-testid="pwhl-clear-btn">
                            <Trash2 size={11} /> Clear
                        </button>
                    </>
                )}
            </div>
            {msg && <div className="text-[11px] text-[#CBD5E1]" data-testid="pwhl-msg">{msg}</div>}
        </div>
    );
}

// Global player search combobox — searches ALL players (not just one team).
function PlayerCombo({ value, players, playerById, candidates, onChange, testId }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const sel = value ? playerById[value] : null;
    const candIds = new Set((candidates || []).map((c) => c.id));
    const ql = q.trim().toLowerCase();
    const matches = useMemo(() => {
        if (!ql) return [...(candidates || []).map((c) => playerById[c.id]).filter(Boolean), ...players].slice(0, 40);
        return players.filter((p) => (p.name || "").toLowerCase().includes(ql)).slice(0, 40);
    }, [ql, players, candidates, playerById]);
    // De-dupe while keeping candidate ordering first.
    const seen = new Set();
    const ordered = matches.filter((p) => p && !seen.has(p.id) && seen.add(p.id));
    return (
        <div className="relative min-w-[220px]" data-testid={testId}>
            <button type="button" onClick={() => { setOpen((o) => !o); setQ(""); }}
                className={inputCls + " text-xs py-1 w-full text-left flex items-center justify-between gap-2"}
                data-testid={`${testId}-trigger`}>
                <span className={sel ? "text-white truncate" : "text-[#64748B]"}>{sel ? `${sel.name} (${abbr(sel.team_id)})` : "— search player —"}</span>
                <span className="text-[#64748B]">▾</span>
            </button>
            {open && (
                <div className="absolute z-30 mt-1 w-[280px] max-h-[280px] overflow-auto rounded border border-[#9D4CDD]/50 bg-[#0B0D14] shadow-xl">
                    <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type a name…"
                        className={inputCls + " text-xs py-1.5 w-full sticky top-0"} data-testid={`${testId}-input`} />
                    <button type="button" onClick={() => { onChange(""); setOpen(false); }}
                        className="block w-full text-left px-2.5 py-1.5 text-[11px] text-[#FB7185] hover:bg-white/5">— unmatched —</button>
                    {ordered.map((p) => (
                        <button key={p.id} type="button" onClick={() => { onChange(p.id); setOpen(false); }}
                            className="block w-full text-left px-2.5 py-1.5 text-xs text-white hover:bg-[#9D4CDD]/20 flex items-center gap-1.5"
                            data-testid={`${testId}-opt-${p.id}`}>
                            {candIds.has(p.id) && <span className="text-[#F5C518]">★</span>}
                            <span className="truncate">{p.name}</span>
                            <span className="text-[#64748B] ml-auto">{abbr(p.team_id)}</span>
                        </button>
                    ))}
                    {!ordered.length && <div className="px-2.5 py-2 text-[11px] text-[#64748B]">No players found.</div>}
                </div>
            )}
        </div>
    );
}

// ===================== player matching review =====================
export function PWHLMatchReview({ sync }) {
    const { preview, overrides, setOverride, matchCount, players, playerById, saveImport, busy, createPlayer } = sync;
    const [creatingId, setCreatingId] = useState("");
    const [createErr, setCreateErr] = useState("");
    if (!preview || !(preview.people || []).length) return null;

    const handleCreate = async (p) => {
        setCreatingId(p.pwhl_id); setCreateErr("");
        try { await createPlayer(p); }
        catch (e) { setCreateErr(`${p.name}: ${e?.response?.data?.detail || e.message}`); }
        finally { setCreatingId(""); }
    };

    // Unmatched first so the admin sees who still needs allocating.
    const rows = [...(preview.people || [])].sort((a, b) => {
        const am = (overrides[a.pwhl_id] ?? a.matched_player_id ?? "") ? 1 : 0;
        const bm = (overrides[b.pwhl_id] ?? b.matched_player_id ?? "") ? 1 : 0;
        return am - bm;
    });

    return (
        <div className="border border-[#1E2233] rounded p-3 bg-black/20" data-testid="pwhl-match-review">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold flex items-center gap-1.5">
                    <Users size={12} className="text-[#9D4CDD]" /> Player Matching — allocate or create
                </div>
                <div className="flex items-center gap-3 text-[11px]">
                    <span><span className="text-[#22C55E]">{matchCount.matched} matched</span>{" · "}
                        <span className={matchCount.unmatched ? "text-[#FB7185]" : "text-[#64748B]"} data-testid="pwhl-unmatched-count">{matchCount.unmatched} unmatched</span></span>
                    <button type="button" onClick={saveImport} disabled={busy === "save"}
                        className="border border-[#22C55E]/50 text-[#22C55E] hover:bg-[#22C55E]/10 px-2.5 py-1 rounded text-[10px] uppercase tracking-wider font-bold disabled:opacity-50 flex items-center gap-1.5"
                        data-testid="pwhl-match-save-btn">
                        <Save size={11} /> {busy === "save" ? "Saving…" : "Save & sync"}
                    </button>
                </div>
            </div>
            {matchCount.unmatched > 0 && (
                <div className="mb-2 text-[11px] text-[#FBBF24] bg-[#FBBF24]/10 border border-[#FBBF24]/30 rounded px-2.5 py-1.5 flex items-center gap-1.5" data-testid="pwhl-unmatched-banner">
                    <AlertTriangle size={12} /> {matchCount.unmatched} player(s) couldn't be matched — pick an existing player or <strong>+ Create</strong> a new profile, then <strong>Save &amp; sync</strong> so their stats count.
                </div>
            )}
            {createErr && <div className="mb-2 text-[11px] text-[#FB7185]" data-testid="pwhl-create-err">{createErr}</div>}
            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead><tr className="text-[#64748B] uppercase text-[10px]">
                        <th className="px-1.5 py-1 text-left">PWHL Player</th>
                        <th className="px-1.5 text-center">Tm</th>
                        <th className="px-1.5 text-left">Rosterly match</th>
                        <th className="px-1.5 text-center">Status</th>
                    </tr></thead>
                    <tbody>
                        {rows.map((p) => {
                            const sel = overrides[p.pwhl_id] ?? p.matched_player_id ?? "";
                            const isManual = overrides[p.pwhl_id] && overrides[p.pwhl_id] !== p.matched_player_id;
                            const method = sel ? (isManual ? "manual" : (p.match_method === "none" ? "manual" : p.match_method)) : "none";
                            const badge = MATCH_BADGE[method] || MATCH_BADGE.none;
                            const matched = !!(sel && playerById[sel]);
                            return (
                                <tr key={p.pwhl_id} className={`border-t border-[#1E2233]/50 ${matched ? "" : "bg-[#FB7185]/[0.04]"}`} data-testid={`pwhl-match-row-${p.pwhl_id}`}>
                                    <td className="px-1.5 py-1 text-white whitespace-nowrap">{p.name} {p.jersey ? <span className="text-[#64748B]">#{p.jersey}</span> : null}{p.position ? <span className="text-[#64748B]"> · {p.position}</span> : null}</td>
                                    <td className="px-1.5 text-center text-[#94A3B8]">{p.team_code || abbr(p.team_id)}</td>
                                    <td className="px-1.5">
                                        <div className="flex items-center gap-1.5">
                                            <PlayerCombo
                                                value={sel}
                                                players={players}
                                                playerById={playerById}
                                                candidates={p.candidates}
                                                onChange={(v) => setOverride(p.pwhl_id, v)}
                                                testId={`pwhl-match-select-${p.pwhl_id}`}
                                            />
                                            {!matched && (
                                                <button type="button" onClick={() => handleCreate(p)} disabled={creatingId === p.pwhl_id}
                                                    className="shrink-0 border border-[#9D4CDD]/50 text-[#C99BF0] hover:bg-[#9D4CDD]/10 px-2 py-1 rounded text-[10px] uppercase tracking-wider font-bold disabled:opacity-50 flex items-center gap-1"
                                                    data-testid={`pwhl-create-player-${p.pwhl_id}`}>
                                                    <UserPlus size={11} /> {creatingId === p.pwhl_id ? "…" : "New"}
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-1.5 text-center">
                                        <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded border ${badge.cls}`} data-testid={`pwhl-match-status-${p.pwhl_id}`}>
                                            {matched ? badge.label : "unmatched"}
                                        </span>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function StatTable({ title, headers, rows, testId }) {
    return (
        <div className="overflow-x-auto" data-testid={testId}>
            <div className="text-[10px] uppercase tracking-wider text-[#64748B] mb-1">{title}</div>
            <table className="w-full text-xs">
                <thead><tr className="text-[#64748B] uppercase text-[10px]">
                    {headers.map((h) => <th key={h} className="px-1.5 py-1 text-center first:text-left">{h}</th>)}
                </tr></thead>
                <tbody>{rows}</tbody>
            </table>
        </div>
    );
}

// ===================== full admin-page panel =====================
export default function PWHLSyncPanel({ gameId, fixture }) {
    const sync = usePWHLSync({ gameId, fixture });
    const { preview } = sync;
    const m = preview?.meta;

    return (
        <section className="border border-[#9D4CDD]/40 bg-[#9D4CDD]/[0.04] p-4 rounded space-y-4" data-testid="pwhl-sync-panel">
            <PWHLSyncControl sync={sync} />

            {preview && m && (
                <div className="space-y-5">
                    {/* Score header */}
                    <div className="border border-[#1E2233] rounded p-3 bg-black/20" data-testid="pwhl-score-header">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <TeamBadge teamId={m.away_team_id} size="sm" asLink={false} />
                                    <span className="text-white text-sm">{m.away_team_code || "?"}</span>
                                    <span className="font-heading font-black text-2xl text-white tabular-nums">{m.away_score}</span>
                                </div>
                                <span className="text-[#64748B]">@</span>
                                <div className="flex items-center gap-2">
                                    <span className="font-heading font-black text-2xl text-white tabular-nums">{m.home_score}</span>
                                    <span className="text-white text-sm">{m.home_team_code || "?"}</span>
                                    <TeamBadge teamId={m.home_team_id} size="sm" asLink={false} />
                                </div>
                                {m.overtime && <span className="text-[10px] uppercase font-bold text-[#FBBF24]">OT</span>}
                                {m.shootout && <span className="text-[10px] uppercase font-bold text-[#FBBF24]">SO</span>}
                            </div>
                            <div className="text-[11px] text-[#94A3B8] text-right">
                                <div>{m.date} · {m.final ? "Final" : `Status ${m.status}`}</div>
                                <div>{m.venue || "—"}{m.attendance ? ` · ${m.attendance.toLocaleString()} fans` : ""}</div>
                            </div>
                        </div>
                        {(!m.home_team_id || !m.away_team_id) && (
                            <div className="mt-2 text-[10px] text-[#FB7185] flex items-center gap-1">
                                <AlertTriangle size={11} /> One or both PWHL teams could not be mapped to a Rosterly team (codes: {m.away_team_code}/{m.home_team_code}).
                            </div>
                        )}
                        <div className="mt-3 flex gap-1.5 flex-wrap text-[10px]">
                            {(preview.periods || []).map((p) => (
                                <span key={p.period} className="px-2 py-0.5 rounded bg-black/40 border border-[#1E2233] text-[#CBD5E1]" data-testid={`pwhl-period-${p.period}`}>
                                    {p.period <= 3 ? `P${p.period}` : p.period === 4 ? "OT" : "SO"}: {p.away}-{p.home}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Team stats */}
                    <div className="grid grid-cols-2 gap-3" data-testid="pwhl-team-stats">
                        {[["away", m.away_team_code], ["home", m.home_team_code]].map(([k, code]) => {
                            const t = preview.team_stats?.[k] || {};
                            return (
                                <div key={k} className="border border-[#1E2233] rounded p-2 text-xs">
                                    <div className="text-[#94A3B8] font-bold mb-1">{code}</div>
                                    <div className="grid grid-cols-3 gap-1 text-[#CBD5E1]">
                                        <span>Shots {t.shots ?? 0}</span><span>Hits {t.hits ?? 0}</span><span>PIM {t.pim ?? 0}</span>
                                        <span>FO {t.fo_won ?? 0}/{t.fo_att ?? 0}</span><span>PPG {t.pp_goals ?? 0}</span><span>PP {t.pp_attempts ?? 0}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Goalies */}
                    <StatTable
                        title={`Goalies (${(preview.goalies || []).length})`} testId="pwhl-goalies-table"
                        headers={["Goalie", "Team", "Dec", "SA", "SV", "GA", "TOI"]}
                        rows={(preview.goalies || []).map((g, i) => (
                            <tr key={i} className="border-t border-[#1E2233]/50">
                                <td className="px-1.5 py-1 text-white whitespace-nowrap">{g.player?.name}</td>
                                <td className="px-1.5 text-center text-[#94A3B8]">{g.team_code}</td>
                                <td className="px-1.5 text-center text-[#CBD5E1]">{g.decision || "—"}</td>
                                <td className="px-1.5 text-center text-white">{g.shots_against}</td>
                                <td className="px-1.5 text-center text-white">{g.saves}</td>
                                <td className="px-1.5 text-center text-white">{g.goals_against}</td>
                                <td className="px-1.5 text-center text-white">{g.toi || "—"}</td>
                            </tr>
                        ))}
                    />

                    {/* Skaters */}
                    <StatTable
                        title={`Skaters (${(preview.skaters || []).length})`} testId="pwhl-skaters-table"
                        headers={["#", "Skater", "Pos", "Tm", "G", "A", "P", "+/-", "PIM", "SOG", "Sh", "Hits", "Blk", "FOW/A"]}
                        rows={(preview.skaters || []).map((s, i) => (
                            <tr key={i} className="border-t border-[#1E2233]/50">
                                <td className="px-1.5 py-1 text-center text-[#64748B]">{s.player?.jersey}</td>
                                <td className="px-1.5 text-white whitespace-nowrap">{s.player?.name}</td>
                                <td className="px-1.5 text-center text-[#94A3B8]">{s.player?.position}</td>
                                <td className="px-1.5 text-center text-[#94A3B8]">{s.team_code}</td>
                                <td className="px-1.5 text-center text-white">{s.g}</td>
                                <td className="px-1.5 text-center text-white">{s.a}</td>
                                <td className="px-1.5 text-center text-white font-bold">{s.p}</td>
                                <td className="px-1.5 text-center text-[#CBD5E1]">{s.plus_minus}</td>
                                <td className="px-1.5 text-center text-[#CBD5E1]">{s.pim}</td>
                                <td className="px-1.5 text-center text-[#CBD5E1]">{s.sog}</td>
                                <td className="px-1.5 text-center text-[#CBD5E1]">{s.shots}</td>
                                <td className="px-1.5 text-center text-[#CBD5E1]">{s.hits}</td>
                                <td className="px-1.5 text-center text-[#CBD5E1]">{s.blocks}</td>
                                <td className="px-1.5 text-center text-[#CBD5E1]">{s.fow}/{s.foa}</td>
                            </tr>
                        ))}
                    />

                    <PWHLMatchReview sync={sync} />
                </div>
            )}
        </section>
    );
}
